import { ArrowLeft, ShieldCheck } from 'lucide-react';
import * as React from 'react';
import { Controller } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { DemoLoginButton } from '@/features/demo';
import { isApiError } from '@/shared/api';
import { FormRow, fieldA11yProps } from '@/shared/components/form/form-row';
import { OtpInput } from '@/shared/components/form/otp-input';
import { PhoneInput } from '@/shared/components/form/phone-input';
import { Alert } from '@/shared/components/ui/alert';
import { Button } from '@/shared/components/ui/button';
import { toast } from '@/shared/components/ui/toaster';
import { useCountdown } from '@/shared/hooks/use-countdown';
import { applyServerErrors, useZodForm } from '@/shared/hooks/use-zod-form';
import { ROUTES } from '@/shared/constants/routes';
import { formatPhone, toPersianDigits } from '@/shared/utils/formatters';

import { useMiniAppLink, useRequestOtp, useVerifyOtp } from '../api/auth.queries';
import { BrandMark } from '../components/brand-mark';
import { useMiniApp } from '../hooks/use-mini-app';
import { requestOtpSchema, verifyOtpSchema } from '../validation/auth.schema';

/** Backend allows 3 OTP requests per 10 minutes; 120s keeps users inside that. */
const RESEND_COOLDOWN_SECONDS = 120;

type Step = 'phone' | 'code';

/**
 * Phone + OTP sign-in, in two steps on one route.
 *
 * Kept as a single route deliberately: the phone number never has to survive a
 * navigation, so a refresh or an accidental back-swipe cannot strand the user
 * on a code screen with nothing to verify against.
 *
 * Inside the Eitaa mini app the same screen doubles as the identity-linking
 * step: when `/auth/miniapp` answers 428 the platform gave us no phone number,
 * and the verified code is sent to `/auth/miniapp/link` instead.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { initData } = useMiniApp();

  const [step, setStep] = React.useState<Step>('phone');
  const [phone, setPhone] = React.useState('');
  const cooldown = useCountdown(RESEND_COOLDOWN_SECONDS);

  const requestOtp = useRequestOtp();
  const verifyOtp = useVerifyOtp();
  const miniAppLink = useMiniAppLink();

  // `/auth/miniapp` returned 428: the code must bind the two identities rather
  // than start a plain session.
  const linkingMiniApp = Boolean(
    initData && (location.state as { linkMiniApp?: boolean } | null)?.linkMiniApp,
  );

  const redirectTo = (location.state as { from?: string } | null)?.from ?? ROUTES.root;

  const phoneForm = useZodForm(requestOtpSchema, { defaultValues: { phone: '' } });
  const codeForm = useZodForm(verifyOtpSchema, { defaultValues: { code: '' } });

  const sendCode = async (targetPhone: string) => {
    await requestOtp.mutateAsync({ phone: targetPhone });
    setPhone(targetPhone);
    setStep('code');
    cooldown.start();
    codeForm.reset({ code: '' });
  };

  const onSubmitPhone = phoneForm.handleSubmit(async (values) => {
    try {
      await sendCode(values.phone);
    } catch (error) {
      applyServerErrors(phoneForm, error, { knownFields: ['phone'] });
    }
  });

  const onSubmitCode = codeForm.handleSubmit(async (values) => {
    try {
      if (linkingMiniApp && initData) {
        await miniAppLink.mutateAsync({ init_data: initData, phone, code: values.code });
      } else {
        await verifyOtp.mutateAsync({ phone, code: values.code });
      }
      // `replace` so the back gesture cannot return to a spent code screen.
      void navigate(redirectTo, { replace: true });
    } catch (error) {
      codeForm.setValue('code', '');
      applyServerErrors(codeForm, error, { knownFields: ['code'] });
    }
  });

  const resend = async () => {
    if (cooldown.isRunning) return;
    try {
      await sendCode(phone);
      toast.success('کد تأیید دوباره ارسال شد');
    } catch (error) {
      toast.error(isApiError(error) ? error.message : 'ارسال مجدد کد ناموفق بود');
    }
  };

  const codeValue = codeForm.watch('code') ?? '';
  const submitting = verifyOtp.isPending || miniAppLink.isPending;

  return (
    <div className="app-column flex flex-col justify-center gap-8 px-6 py-10">
      <header className="flex flex-col items-center gap-4 text-center">
        <BrandMark size="lg" halo />
        <div className="space-y-1.5">
          <h1 className="text-xl font-bold">
            {step === 'phone' ? 'ورود به شارژر' : 'کد تأیید را وارد کنید'}
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed text-balance">
            {step === 'phone'
              ? 'برای مدیریت شارژ ساختمان، شماره موبایل خود را وارد کنید.'
              : `کد ۶ رقمی ارسال‌شده به ${formatPhone(phone)} را وارد کنید.`}
          </p>
        </div>
      </header>

      {step === 'phone' ? (
        <form onSubmit={(event) => void onSubmitPhone(event)} className="space-y-5" noValidate>
          {linkingMiniApp ? (
            <Alert variant="info" title="تأیید شماره موبایل">
              برای اتصال حساب ایتای شما به شارژر، یک‌بار شماره موبایل خود را تأیید کنید.
            </Alert>
          ) : null}

          <Controller
            control={phoneForm.control}
            name="phone"
            render={({ field, fieldState }) => (
              <FormRow
                htmlFor="phone"
                label="شماره موبایل"
                error={fieldState.error?.message}
                required
              >
                <PhoneInput
                  {...fieldA11yProps('phone', fieldState.error?.message)}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  autoFocus
                />
              </FormRow>
            )}
          />

          {phoneForm.formState.errors.root?.serverError ? (
            <Alert variant="destructive">
              {phoneForm.formState.errors.root.serverError.message}
            </Alert>
          ) : null}

          <Button type="submit" block size="lg" loading={requestOtp.isPending}>
            دریافت کد تأیید
          </Button>

          <p className="text-muted-foreground flex items-center justify-center gap-1.5 text-xs">
            <ShieldCheck className="size-3.5" aria-hidden />
            ورود شما به معنی پذیرش قوانین و حریم خصوصی شارژر است.
          </p>

          <DemoLoginButton />
        </form>
      ) : (
        <form onSubmit={(event) => void onSubmitCode(event)} className="space-y-5" noValidate>
          <Controller
            control={codeForm.control}
            name="code"
            render={({ field, fieldState }) => (
              <FormRow
                htmlFor="otp"
                label="کد تأیید"
                error={fieldState.error?.message}
                className="[&>label]:justify-center"
              >
                <OtpInput
                  id="otp"
                  aria-describedby="otp-message"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onComplete={() => void onSubmitCode()}
                  invalid={Boolean(fieldState.error)}
                  disabled={submitting}
                  autoFocus
                />
              </FormRow>
            )}
          />

          {codeForm.formState.errors.root?.serverError ? (
            <Alert variant="destructive">
              {codeForm.formState.errors.root.serverError.message}
            </Alert>
          ) : null}

          <Button
            type="submit"
            block
            size="lg"
            loading={submitting}
            disabled={codeValue.length < 6}
          >
            ورود
          </Button>

          <div className="flex items-center justify-between text-xs">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="px-0"
              onClick={() => {
                setStep('phone');
                cooldown.reset();
              }}
            >
              <ArrowLeft className="size-3.5 rtl:rotate-180" />
              تغییر شماره
            </Button>

            <Button
              type="button"
              variant="link"
              size="sm"
              className="px-0"
              disabled={cooldown.isRunning || requestOtp.isPending}
              onClick={() => void resend()}
            >
              {cooldown.isRunning
                ? `ارسال مجدد تا ${toPersianDigits(cooldown.remaining)} ثانیه`
                : 'ارسال مجدد کد'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
